/**
 * BiblePlanDayBuilder — friendly UI for building a Bible reading plan's day-by-day
 * passages WITHOUT writing JSON.
 *
 * Two modes: 
 *   "builder" (default) — clickable rows for Day → Passages (book, chapter, verses) + note
 *   "json"   — raw JSON for power users (kept for backward compat)
 *
 * Also supports uploading a CSV or JSON file from the user's device:
 *   CSV columns: day,book,chapter,verse_start,verse_end,note
 *   JSON: same shape as the API expects.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Upload, Code2, ListPlus, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// 66 books — Old + New Testament. Used for the dropdown. Source-of-truth shared
// with backend BIBLE_BOOKS.
const BIBLE_BOOKS = [
  // OT
  'Genesis','Exodus','Leviticus','Numbers','Deuteronomy','Joshua','Judges','Ruth',
  '1 Samuel','2 Samuel','1 Kings','2 Kings','1 Chronicles','2 Chronicles','Ezra','Nehemiah',
  'Esther','Job','Psalms','Proverbs','Ecclesiastes','Song of Solomon','Isaiah','Jeremiah',
  'Lamentations','Ezekiel','Daniel','Hosea','Joel','Amos','Obadiah','Jonah','Micah','Nahum',
  'Habakkuk','Zephaniah','Haggai','Zechariah','Malachi',
  // NT
  'Matthew','Mark','Luke','John','Acts','Romans','1 Corinthians','2 Corinthians','Galatians',
  'Ephesians','Philippians','Colossians','1 Thessalonians','2 Thessalonians','1 Timothy',
  '2 Timothy','Titus','Philemon','Hebrews','James','1 Peter','2 Peter','1 John','2 John',
  '3 John','Jude','Revelation',
];

const blankPassage = () => ({ book: 'John', chapter: 1, verse_start: '', verse_end: '' });
const blankDay = (n) => ({ day: n, note: '', passages: [blankPassage()] });

function parseCsv(text) {
  // Strip UTF-8 BOM if present (Excel exports often have it)
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const rows = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (!rows.length) return [];
  const header = rows[0].toLowerCase().split(',').map(s => s.trim().replace(/^\uFEFF/, ''));
  const idx = (k) => header.indexOf(k);
  // Accept "day", "date" or "day_number" as the day column header
  const dayCol = idx('day') >= 0 ? idx('day')
              : idx('date') >= 0 ? idx('date')
              : idx('day_number');
  const bookCol = idx('book'); const chCol = idx('chapter');
  const vsCol = idx('verse_start'); const veCol = idx('verse_end'); const noteCol = idx('note');
  if (dayCol < 0 || bookCol < 0 || chCol < 0) {
    throw new Error('CSV must have at least: day (or date), book, chapter');
  }

  // First pass: detect whether the "day" column is a numeric day index
  // (1, 2, 3...) or an ISO date (YYYY-MM-DD). If dates, we map the EARLIEST
  // unique date to Day 1 and increment sequentially.
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  const sample = (rows[1] || '').split(',')[dayCol]?.trim() || '';
  const usingDates = ISO_DATE.test(sample);

  // Build a date→dayNumber mapping when needed (skipping Sundays still counted
  // as their own day, since "Sundays rest" is encoded by the absence of rows
  // for those dates in the upstream CSV — we just trust the file).
  const dateToDayNum = new Map();
  if (usingDates) {
    const uniqueDates = [];
    const seen = new Set();
    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i].split(',').map(c => c.trim());
      const v = cells[dayCol];
      if (v && ISO_DATE.test(v) && !seen.has(v)) { seen.add(v); uniqueDates.push(v); }
    }
    uniqueDates.sort();
    uniqueDates.forEach((d, i) => dateToDayNum.set(d, i + 1));
  }

  const dayMap = new Map();
  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i].split(',').map(c => c.trim());
    const rawDay = cells[dayCol];
    let dayNum;
    if (usingDates) {
      dayNum = dateToDayNum.get(rawDay);
    } else {
      dayNum = Number(rawDay);
    }
    if (!dayNum) continue;
    if (!dayMap.has(dayNum)) {
      dayMap.set(dayNum, {
        day: dayNum,
        // Keep the original date as a note prefix so the admin can see it
        date: usingDates ? rawDay : '',
        note: '',
        passages: [],
      });
    }
    const day = dayMap.get(dayNum);
    if (noteCol >= 0 && cells[noteCol] && !day.note) day.note = cells[noteCol];
    day.passages.push({
      book: cells[bookCol] || 'Genesis',
      chapter: Number(cells[chCol]) || 1,
      verse_start: vsCol >= 0 && cells[vsCol] ? Number(cells[vsCol]) : '',
      verse_end: veCol >= 0 && cells[veCol] ? Number(cells[veCol]) : '',
    });
  }
  // Drop the helper "date" key on the way out — schema only knows day/note/passages
  return Array.from(dayMap.values())
    .sort((a, b) => a.day - b.day)
    .map(({ date, ...rest }) => date ? { ...rest, note: rest.note ? `${date} · ${rest.note}` : date } : rest);
}

const cleanForApi = (days) => days.map(d => ({
  day: Number(d.day),
  note: d.note || '',
  passages: (d.passages || []).map(p => ({
    book: p.book,
    chapter: Number(p.chapter) || 1,
    verse_start: p.verse_start ? Number(p.verse_start) : null,
    verse_end: p.verse_end ? Number(p.verse_end) : null,
  })).filter(p => p.book && p.chapter),
})).filter(d => d.day && d.passages.length);

export default function BiblePlanDayBuilder({ value, onChange }) {
  const [mode, setMode] = useState('builder'); // 'builder' | 'json'
  const [jsonText, setJsonText] = useState(() => JSON.stringify(value || [], null, 2));
  const fileRef = useRef(null);

  const days = Array.isArray(value) ? value : [];

  const updateDays = (next) => {
    onChange(next);
    setJsonText(JSON.stringify(next, null, 2));
  };

  const addDay = () => {
    const nextDayNum = (days[days.length - 1]?.day || 0) + 1;
    updateDays([...days, blankDay(nextDayNum)]);
  };
  const removeDay = (idx) => updateDays(days.filter((_, i) => i !== idx));
  const updateDay = (idx, patch) => {
    const next = days.slice();
    next[idx] = { ...next[idx], ...patch };
    updateDays(next);
  };
  const addPassage = (dayIdx) => {
    const next = days.slice();
    next[dayIdx] = { ...next[dayIdx], passages: [...(next[dayIdx].passages || []), blankPassage()] };
    updateDays(next);
  };
  const updatePassage = (dayIdx, pIdx, patch) => {
    const next = days.slice();
    const passages = next[dayIdx].passages.slice();
    passages[pIdx] = { ...passages[pIdx], ...patch };
    next[dayIdx] = { ...next[dayIdx], passages };
    updateDays(next);
  };
  const removePassage = (dayIdx, pIdx) => {
    const next = days.slice();
    next[dayIdx] = { ...next[dayIdx], passages: next[dayIdx].passages.filter((_, i) => i !== pIdx) };
    updateDays(next);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await f.text();
      let parsed;
      if (f.name.toLowerCase().endsWith('.csv') || /^\uFEFF?(day|date),/i.test(text)) {
        parsed = parseCsv(text);
      } else {
        parsed = JSON.parse(text);
        if (!Array.isArray(parsed)) throw new Error('JSON must be an array of day objects');
      }
      updateDays(parsed);
      toast.success(`Imported ${parsed.length} day${parsed.length === 1 ? '' : 's'} from ${f.name}`);
    } catch (err) {
      toast.error(`Could not parse ${f.name}: ${err.message}`);
    } finally {
      e.target.value = ''; // reset
    }
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText || '[]');
      if (!Array.isArray(parsed)) throw new Error('Must be an array');
      updateDays(parsed);
      toast.success('JSON applied');
      setMode('builder');
    } catch (e) {
      toast.error(`Invalid JSON: ${e.message}`);
    }
  };

  const downloadCsv = () => {
    const cleaned = cleanForApi(days);
    const lines = ['day,book,chapter,verse_start,verse_end,note'];
    cleaned.forEach((d) => {
      d.passages.forEach((p, i) => {
        lines.push([
          d.day, p.book, p.chapter,
          p.verse_start ?? '', p.verse_end ?? '',
          i === 0 ? (d.note || '').replace(/[,\n]/g, ' ') : '',
        ].join(','));
      });
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'reading-plan.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const totalPassages = useMemo(() => days.reduce((s, d) => s + (d.passages?.length || 0), 0), [days]);

  return (
    <div className="space-y-3" data-testid="bible-plan-day-builder">
      {/* Header / mode toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Label className="!text-xs uppercase tracking-wider text-slate-500">Days</Label>
          <Badge variant="outline" data-testid="day-builder-count">
            {days.length} day{days.length === 1 ? '' : 's'} · {totalPassages} passage{totalPassages === 1 ? '' : 's'}
          </Badge>
        </div>
        <div className="flex gap-1">
          <Button
            type="button" variant={mode === 'builder' ? 'default' : 'outline'}
            size="sm" onClick={() => setMode('builder')}
            data-testid="day-builder-mode-builder"
          >
            <ListPlus className="h-3.5 w-3.5 mr-1" /> Builder
          </Button>
          <Button
            type="button" variant={mode === 'json' ? 'default' : 'outline'}
            size="sm" onClick={() => { setJsonText(JSON.stringify(days, null, 2)); setMode('json'); }}
            data-testid="day-builder-mode-json"
          >
            <Code2 className="h-3.5 w-3.5 mr-1" /> JSON
          </Button>
        </div>
      </div>

      {/* Tools row */}
      <div className="flex flex-wrap gap-2 items-center bg-slate-50 border rounded-md p-2">
        <input
          ref={fileRef} type="file" accept=".json,.csv,application/json,text/csv"
          className="hidden" onChange={onFile} data-testid="day-builder-file-input"
        />
        <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="day-builder-upload-btn">
          <Upload className="h-3.5 w-3.5 mr-1" /> Upload CSV / JSON
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={downloadCsv} disabled={!days.length} data-testid="day-builder-download-btn">
          <Download className="h-3.5 w-3.5 mr-1" /> Download CSV
        </Button>
        <span className="text-[11px] text-slate-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> CSV columns: <code className="bg-white px-1 rounded">day,book,chapter,verse_start,verse_end,note</code>
        </span>
      </div>

      {/* Builder mode */}
      {mode === 'builder' && (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="day-builder-list">
          {days.length === 0 && (
            <div className="text-center p-6 border-2 border-dashed border-slate-200 rounded-md text-sm text-slate-500">
              No days yet. Click <strong>Add day</strong> below or upload a CSV.
            </div>
          )}
          {days.map((d, dIdx) => (
            <div key={dIdx} className="border rounded-md bg-white" data-testid={`day-row-${dIdx}`}>
              <div className="flex items-center gap-2 p-2 bg-slate-50 border-b">
                <Label className="text-xs">Day</Label>
                <Input
                  type="number" min={1} value={d.day}
                  onChange={(e) => updateDay(dIdx, { day: Number(e.target.value) || 1 })}
                  className="h-7 w-16 text-sm"
                  data-testid={`day-num-${dIdx}`}
                />
                <Input
                  placeholder="Optional note for this day"
                  value={d.note || ''}
                  onChange={(e) => updateDay(dIdx, { note: e.target.value })}
                  className="h-7 flex-1 text-sm"
                  data-testid={`day-note-${dIdx}`}
                />
                <Button
                  type="button" variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                  onClick={() => removeDay(dIdx)}
                  title="Remove day" data-testid={`day-remove-${dIdx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="p-2 space-y-1">
                {(d.passages || []).map((p, pIdx) => (
                  <div key={pIdx} className="flex items-center gap-1.5" data-testid={`passage-${dIdx}-${pIdx}`}>
                    <select
                      className="h-7 text-sm border rounded px-1 bg-white"
                      value={p.book}
                      onChange={(e) => updatePassage(dIdx, pIdx, { book: e.target.value })}
                      data-testid={`passage-book-${dIdx}-${pIdx}`}
                    >
                      {BIBLE_BOOKS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <Input
                      type="number" min={1} placeholder="Ch."
                      value={p.chapter} className="h-7 w-16 text-sm"
                      onChange={(e) => updatePassage(dIdx, pIdx, { chapter: Number(e.target.value) || 1 })}
                      data-testid={`passage-chapter-${dIdx}-${pIdx}`}
                    />
                    <span className="text-slate-400 text-xs">v.</span>
                    <Input
                      type="number" min={1} placeholder="from"
                      value={p.verse_start || ''} className="h-7 w-16 text-sm"
                      onChange={(e) => updatePassage(dIdx, pIdx, { verse_start: e.target.value })}
                      data-testid={`passage-vs-${dIdx}-${pIdx}`}
                    />
                    <span className="text-slate-400 text-xs">–</span>
                    <Input
                      type="number" min={1} placeholder="to"
                      value={p.verse_end || ''} className="h-7 w-16 text-sm"
                      onChange={(e) => updatePassage(dIdx, pIdx, { verse_end: e.target.value })}
                      data-testid={`passage-ve-${dIdx}-${pIdx}`}
                    />
                    <Button
                      type="button" variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:bg-rose-50"
                      onClick={() => removePassage(dIdx, pIdx)}
                      title="Remove passage" data-testid={`passage-remove-${dIdx}-${pIdx}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button" size="sm" variant="ghost" className="h-7 mt-1 text-indigo-600"
                  onClick={() => addPassage(dIdx)}
                  data-testid={`add-passage-${dIdx}`}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add passage
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button" variant="outline" className="w-full"
            onClick={addDay} data-testid="day-builder-add-day"
          >
            <Plus className="h-4 w-4 mr-1" /> Add day
          </Button>
        </div>
      )}

      {/* JSON mode */}
      {mode === 'json' && (
        <div className="space-y-2">
          <Textarea
            rows={12} className="font-mono text-[11px]"
            value={jsonText} onChange={(e) => setJsonText(e.target.value)}
            data-testid="day-builder-json-textarea"
          />
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={applyJson} data-testid="day-builder-apply-json">
              Apply JSON
            </Button>
            <p className="text-[11px] text-slate-500 self-center">
              Format: <code>{`[{"day":1,"passages":[{"book":"John","chapter":1}],"note":"…"}]`}</code>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// re-export for power users to inspect
export { BIBLE_BOOKS, parseCsv, cleanForApi };
